import {
  faBox,
  faUsers,
  faStore,
  faChartBar,
  faCalendar,
  faCalendarDays,
  faCalendarWeek,
  faChevronDown,
  faArrowTrendUp,
  faArrowTrendDown
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { RecentSalesTable } from "../../components/recent-sales-table";
import { DashboardChart } from "../../components/dashboard-chart";
import { useAppOverview } from "../../hooks";

const MONTHS = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

const TIME_RANGES = [
  { value: "daily", label: "Daily", icon: faCalendarDays },
  { value: "monthly", label: "Monthly", icon: faCalendarWeek },
  { value: "yearly", label: "Yearly", icon: faCalendar },
];

const Dropdown = ({ options, value, onChange, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs sm:text-sm bg-white border border-gray-300 rounded-lg hover:border-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-800/30 whitespace-nowrap"
      >
        {value?.label || value || "Select"}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`h-2.5 w-2.5 sm:h-3 sm:w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-1 z-50 min-w-[80px] sm:min-w-[100px] max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
            {options.map((option) => (
              <button
                key={option.value || option}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-blue-50 transition-colors ${
                  (option.value || option) === (value?.value || value)
                    ? "bg-blue-50 text-blue-800 font-medium"
                    : "text-gray-700"
                }`}
              >
                {option.label || option}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const AdminHome = () => {
  const {
    stats,
    chart,
    recentSales,
    availableYears,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    timeRange,
    setTimeRange,
  } = useAppOverview();

  const [localTimeRange, setLocalTimeRange] = useState(TIME_RANGES[1]);

  useEffect(() => {
    document.title = "Dashboard - Shop Kiganjani";
  }, []);


  const hasAutoSelectedYear = useRef(false);

  useEffect(() => {
    if (hasAutoSelectedYear.current) return;
    if (availableYears.length === 0) return;

    const latest = String(availableYears[availableYears.length - 1]);
    setSelectedYear(latest);
    hasAutoSelectedYear.current = true;
  }, [availableYears, setSelectedYear]);

  useEffect(() => {
    if (localTimeRange.value !== timeRange) {
      setTimeRange(localTimeRange.value);
    }
  }, [localTimeRange, timeRange, setTimeRange]);

  const currentMonthLabel =
    MONTHS.find((m) => m.value === selectedMonth)?.label || "Jan";

  const statCards = useMemo(
    () => [
      {
        title: "Total Users",
        value: Number(stats.total_users || 0).toLocaleString(),
        change: "",
        trending: "up",
        icon: faUsers,
        color: "text-blue-600",
        bgColor: "bg-blue-100",
      },
      {
        title: "Total Shops",
        value: Number(stats.total_shops || 0).toLocaleString(),
        change: "",
        trending: "up",
        icon: faStore,
        color: "text-green-600",
        bgColor: "bg-green-100",
      },
      {
        title: "Total Stock",
        value: Number(stats.total_stock || 0).toLocaleString(),
        change: "",
        trending: "up",
        icon: faBox,
        color: "text-yellow-600",
        bgColor: "bg-yellow-100",
      },
      {
        title: "Total Sales",
        value: Number(stats.total_sales || 0).toLocaleString(),
        change: "",
        trending: "up",
        icon: faChartBar,
        color: "text-red-600",
        bgColor: "bg-red-100",
      },
    ],
    [stats],
  );

  const handleYearChange = (option) => {
    setSelectedYear(String(option.value || option));
  };

  const handleMonthChange = (option) => {
    setSelectedMonth(Number(option.value || option));
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
          Dashboard Overview
        </h2>
        <p className="text-sm text-gray-600 mt-0.5">
          Welcome back,
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card key={stat.title} className="border-gray-200 bg-white">
            <CardContent className="p-3 sm:p-4 lg:p-5">
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-0.5 sm:gap-1 min-w-0">
                  <span className="text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider truncate">
                    {stat.title}
                  </span>
                  <span className="text-base sm:text-xl lg:text-2xl font-bold text-gray-800 truncate">
                    {stat.value}
                  </span>
                  {stat.change && (
                    <div className="flex items-center gap-0.5 sm:gap-1 flex-wrap">
                      {stat.trending === "up" ? (
                        <FontAwesomeIcon
                          icon={faArrowTrendUp}
                          className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-green-600"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={faArrowTrendDown}
                          className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-red-600"
                        />
                      )}
                      <span
                        className={`text-[10px] sm:text-xs font-medium ${
                          stat.trending === "up"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {stat.change}
                      </span>
                      <span className="text-[10px] sm:text-xs text-gray-600">
                        vs last month
                      </span>
                    </div>
                  )}
                </div>
                <div
                  className={`flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-lg ${stat.bgColor} shrink-0`}
                >
                  <FontAwesomeIcon
                    icon={stat.icon}
                    className={`h-4 w-4 sm:h-5 sm:w-5 ${stat.color}`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 xl:grid-cols-7">
        <Card className="border-gray-200 bg-white xl:col-span-4">
          <CardHeader className="pb-2 sm:pb-3">
            <div className="flex flex-col gap-3">
              <CardTitle className="text-base font-semibold text-gray-800">
                Revenue Overview
              </CardTitle>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5 flex-wrap">
                  {TIME_RANGES.map((range) => (
                    <button
                      key={range.value}
                      onClick={() => setLocalTimeRange(range)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] sm:text-xs font-medium transition-all duration-200 ${
                        localTimeRange.value === range.value
                          ? "bg-blue-800 text-white shadow-sm"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={range.icon}
                        className="h-2.5 w-2.5 sm:h-3 sm:w-3"
                      />
                      <span className="hidden xs:inline">{range.label}</span>
                    </button>
                  ))}
                </div>

                <Dropdown
                  options={
                    availableYears.length > 0
                      ? availableYears.map((y) => String(y))
                      : [String(new Date().getFullYear())]
                  }
                  value={selectedYear}
                  onChange={handleYearChange}
                  className="w-16 sm:w-20"
                />

                {localTimeRange.value === "daily" && (
                  <Dropdown
                    options={MONTHS}
                    value={currentMonthLabel}
                    onChange={handleMonthChange}
                    className="w-16 sm:w-20"
                  />
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="w-full h-[250px] sm:h-[280px] lg:h-[300px]">
              <DashboardChart
                chart={chart}
                timeRange={localTimeRange.value}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white xl:col-span-3">
          <CardHeader className="pb-2 sm:pb-3">
            <CardTitle className="text-base font-semibold text-gray-800">
              Recent Sales
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <RecentSalesTable recentSales={recentSales} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminHome;